<template>
  <div v-editable="blok" class="legal">
    <aside class="side-nav">
      <div class="side-nav__box">
        <p class="heading--subheading">
          {{ blok.subtitle }}
        </p>
        <nav class="legal__table-of-contents">
          <ul class="">
            <li
              v-for="item in blok.blocks"
              :key="item._uid"
              class="side-nav__list-item"
            >
              <nuxt-link
                prefetch
                :to="{ hash: `#${slugifyItem(item.title)}` }"
                class="scrollactive-item side-nav__item"
                :class="{
                  'is-active': isActive(slugifyItem(item.title)),
                }"
                @click="setActive(slugifyItem(item.title))"
                >{{ item.title }}</nuxt-link
              >
            </li>
          </ul>
        </nav>
      </div>
    </aside>
    <div class="legal__main">
      <ol class="legal__list">
        <StoryblokComponent
          v-for="childBlok in blok.blocks"
          :key="childBlok._uid"
          :blok="childBlok"
        />
      </ol>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useActiveScroll } from 'vue-use-active-scroll'
import type { SbLegalSectionStoryblok, SbLegalItemStoryblok } from '@/types'

import { slugify } from '@/utils/helpers'

interface Props {
  blok: SbLegalSectionStoryblok
}
const props = defineProps<Props>()

const slugifyItem = (slug: string) => slugify(slug)
const targets = computed(() => {
  return (
    props.blok.blocks?.map((item: SbLegalItemStoryblok) =>
      slugifyItem(item.title),
    ) ?? []
  )
})
const { isActive, setActive } = useActiveScroll(targets, {
  overlayHeight: 100,
})
</script>

<style lang="scss" scoped>
@mixin dot {
  position: absolute;
  top: 16px;
  left: 6px;
  width: 5px;
  height: 5px;
  content: '';
  background-color: var(--blue-500);
  border-radius: 50%;
}

.legal {
  position: relative;
  display: grid;
  column-gap: 40px;

  @media (min-width: $breakpoint-lg) {
    grid-template-columns: 300px 1fr;
  }

  &__main {
    margin-top: 40px;
    overflow-x: hidden;
  }

  &__list {
    counter-reset: h3;
  }
}

.side-nav {
  position: relative;
  height: 100%;

  &__box {
    position: sticky;
    top: calc(#{var(--header-height)} + 40px);
    margin-top: 40px;
    padding-right: 40px;
    border-bottom: 1px solid var(--gray-400);
    padding-bottom: 40px;

    @media (min-width: $breakpoint-lg) {
      padding-bottom: 0;
      border-bottom: 0;
      border-right: 1px solid var(--gray-400);
    }
  }

  &__item {
    @include paragraph-xs;

    color: var(--color-text);
    position: relative;
    display: block;
    padding: 8px 0 8px 24px;

    &::before {
      @include dot;
    }

    @media (min-width: $breakpoint-lg) {
      &::before {
        content: none;
      }
    }

    &:hover {
      color: var(--blue-700);
      text-decoration: none;
      cursor: pointer;
    }
  }

  &__item.is-active {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text);

    &::before {
      @include dot;
    }
  }
}
</style>
